import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { syncEngine } from '@/sync/syncEngine';
import { INITIAL_SYNC_STATUS, type SyncStatus } from '@/sync/types';

interface SyncContextValue extends SyncStatus {
  triggerSyncNow: () => void;
}

const SyncContext = createContext<SyncContextValue | null>(null);

const EXHIBITOR_ROLES = ['ExhibitorAdmin', 'ExhibitorStaff'] as const;

export function SyncProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [status, setStatus] = useState<SyncStatus>(INITIAL_SYNC_STATUS);

  // The engine only makes sense for an authenticated exhibitor session --
  // there's nothing to sync (and no valid token to authenticate with) for
  // an anonymous session or an admin/organiser role that never captures scans.
  const shouldRun = !!user && (EXHIBITOR_ROLES as readonly string[]).includes(user.role);

  useEffect(() => {
    if (!shouldRun) {
      syncEngine.stop();
      return;
    }

    syncEngine.start();
    return () => syncEngine.stop();
  }, [shouldRun]);

  useEffect(() => syncEngine.subscribe(setStatus), []);

  return (
    <SyncContext.Provider value={{ ...status, triggerSyncNow: syncEngine.triggerNow }}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync(): SyncContextValue {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within a SyncProvider');
  return ctx;
}
